/**
 * Calendar client API — sits in front of v9_calendar_events_cache and the
 * cal-sync / cal-list-calendars Netlify functions.
 *
 * Refresh tokens never touch the browser: OAuth kicks off here (full-page
 * redirect to Google or Microsoft) but the token exchange + storage happen
 * inside `oauth-callback`.
 */
import { getSupabase } from './supabase';

export type CalendarSource = 'google' | 'office365' | 'crm';

export interface CalendarEvent {
  id: string;
  source: CalendarSource;
  external_id: string;
  title: string;
  start_at: string;
  end_at: string;
  location: string | null;
  attendees: { name: string | null; email: string | null; type: string | null }[];
  url: string | null;
  is_all_day: boolean;
  raw: Record<string, unknown>;
}

const GOOGLE_SIDEBAR_SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/gmail.labels',
  'openid',
  'email',
].join(' ');

const MICROSOFT_SCOPES = [
  'Calendars.Read',
  'Mail.Read',
  'MailboxSettings.Read',
  'User.Read',
  'offline_access',
].join(' ');

function generateNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Kick off Google OAuth for the sidebar (calendar + gmail scopes). Distinct
 * state shape vs the sheets flow so the callback knows where to persist.
 */
export function connectGoogle(userId: string, tenantId: string): void {
  const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
  if (!clientId) {
    throw new Error('[calendar] VITE_GOOGLE_OAUTH_CLIENT_ID is not set.');
  }
  const state = `${userId}:sidebar:${tenantId}:${generateNonce()}`;
  sessionStorage.setItem('v9.google_sidebar_state', state);
  const redirectUri = `${window.location.origin}/.netlify/functions/oauth-callback?provider=google`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: GOOGLE_SIDEBAR_SCOPES,
    access_type: 'offline',
    prompt: 'consent',
    include_granted_scopes: 'true',
    state,
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export function connectOffice365(userId: string, tenantId: string): void {
  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
  if (!clientId) {
    throw new Error('[calendar] VITE_MICROSOFT_CLIENT_ID is not set.');
  }
  const tenantParam = (import.meta.env.VITE_MICROSOFT_TENANT_ID as string | undefined) ?? 'common';
  const state = `${userId}:sidebar:${tenantId}:${generateNonce()}`;
  sessionStorage.setItem('v9.office365_state', state);
  const redirectUri = `${window.location.origin}/.netlify/functions/oauth-callback?provider=office365`;
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    response_mode: 'query',
    scope: MICROSOFT_SCOPES,
    state,
    prompt: 'consent',
  });
  window.location.href = `https://login.microsoftonline.com/${encodeURIComponent(tenantParam)}/oauth2/v2.0/authorize?${params.toString()}`;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('[calendar] not authenticated');
  return { authorization: `Bearer ${token}`, 'content-type': 'application/json' };
}

/**
 * Read cached calendar events for the signed-in user within [start, end].
 * The sync function keeps the cache fresh every 15 minutes; this is a pure
 * Supabase read (RLS scopes to the current user inside their tenant).
 */
export async function listCalendarEvents(opts: {
  tenantId: string;
  startIso: string;
  endIso: string;
}): Promise<CalendarEvent[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('v9_calendar_events_cache')
    .select('id, source, external_id, title, start_at, end_at, location, attendees, url, is_all_day, raw')
    .eq('tenant_id', opts.tenantId)
    .gte('start_at', opts.startIso)
    .lte('start_at', opts.endIso)
    .order('start_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as CalendarEvent[];
}

export interface ConnectionStatus {
  google: { connected: boolean; calendars?: { id: string; summary: string; primary?: boolean }[]; email?: string | null; error?: string };
  office365: { connected: boolean; calendars?: { id: string; name: string; isDefaultCalendar?: boolean }[]; upn?: string | null; error?: string };
}

export async function listConnectedCalendars(): Promise<ConnectionStatus> {
  const res = await fetch('/.netlify/functions/cal-list-calendars', {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`list calendars failed (${res.status})`);
  return (await res.json()) as ConnectionStatus;
}

export async function triggerCalSync(): Promise<{ google: { synced: number }; office365: { synced: number } }> {
  const res = await fetch('/.netlify/functions/cal-sync', {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`cal-sync failed (${res.status})`);
  return (await res.json()) as { google: { synced: number }; office365: { synced: number } };
}
